import { graphQlClient } from '../../config/graphqlClient';
import { SubscriptionGraphQL } from '../../graphql/subscription.queries';
import type {
  OrganizationSubscription,
  PaymentMethod,
  SubscriptionTransaction,
  SubscriptionAnalytics,
  PricingConfig,
  SubscriptionPlanTemplate,
  ActiveSeat,
  PlanAddonSummary,
} from '../../models/subscription';

export const subscriptionService = {
  /**
   * Get subscription for current organization (NEW SYSTEM)
   */
  async getSubscription(): Promise<OrganizationSubscription | null> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getOrganizationSubscription);
    return data.getOrganizationSubscription;
  },

  /**
   * Get subscription analytics (Super Admin)
   */
  async getSubscriptionAnalytics(): Promise<SubscriptionAnalytics> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getSubscriptionAnalytics);
    return data.getSubscriptionAnalytics;
  },

  /**
   * Get active seats for organization
   */
  async getActiveSeats(organizationId?: string): Promise<ActiveSeat[]> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getActiveSeats, {
      organizationId,
    });
    return data.getActiveSeats;
  },

  /**
   * Get payment methods for current organization or specified organization
   * @param organizationId Optional - if provided, fetches for that org (requires OWNER role)
   */
  async getPaymentMethods(organizationId?: string): Promise<PaymentMethod[]> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getPaymentMethods, {
      organizationId,
    });
    return data.getPaymentMethods;
  },

  /**
   * Get transaction history for a subscription
   */
  async getTransactionHistory(
    subscriptionId: string,
    page = 0,
    limit = 10,
  ): Promise<SubscriptionTransaction[]> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getTransactionHistory, {
      subscriptionId,
      page,
      limit,
    });
    return data.getTransactionHistory;
  },

  /**
   * Get transaction history for entire organization (all subscriptions)
   */
  async getOrganizationTransactionHistory(
    organizationId?: string,
    page = 1,
    limit = 10,
  ): Promise<SubscriptionTransaction[]> {
    const data: any = await graphQlClient.request(
      SubscriptionGraphQL.queries.getOrganizationTransactionHistory,
      {
        organizationId,
        page,
        limit,
      },
    );
    return data.getOrganizationTransactionHistory;
  },

  /**
   * Cancel subscription (NEW SYSTEM)
   */
  async cancelSubscription(subscriptionId: string): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.cancelOrganizationSubscription, {
      subscriptionId,
    });
    return data.cancelOrganizationSubscription;
  },

  /**
   * Pause subscription (NEW SYSTEM)
   */
  async pauseSubscription(subscriptionId: string): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.pauseOrganizationSubscription, {
      subscriptionId,
    });
    return data.pauseOrganizationSubscription;
  },

  /**
   * Resume paused subscription (NEW SYSTEM)
   */
  async resumeSubscription(subscriptionId: string): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.resumeOrganizationSubscription, {
      subscriptionId,
    });
    return data.resumeOrganizationSubscription;
  },

  /**
   * Add addon subscription (NEW SYSTEM)
   * Creates an independent addon subscription with its own billing period
   */
  async addAddonSubscription(addonId: string, billingPeriod: string): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.addAddonSubscription, {
      addonId,
      billingPeriod,
    });
    return data.addAddonSubscription;
  },

  /**
   * Remove addon subscription (NEW SYSTEM)
   * Cancels an addon subscription by its ID
   */
  async removeAddonSubscription(subscriptionId: string): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.removeAddonSubscription, {
      subscriptionId,
    });
    return data.removeAddonSubscription;
  },

  /**
   * Reactivate a suspended organization's subscription
   * Used by organization OWNERs to restore their organization after suspension
   *
   * Two modes:
   * 1. Use existing payment method: set useExistingPaymentMethod=true and existingPaymentMethodId
   * 2. Add new payment method: provide all billing and credit card fields
   */
  async reactivateSubscription(input: {
    organizationId: string;
    billingCycle: string;
    planId?: string;
    // Option to use existing payment method
    useExistingPaymentMethod?: boolean;
    existingPaymentMethodId?: string;
    // Required only if NOT using existing payment method
    paymentMethodType?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phone?: string;
    ccNumber?: string;
    ccExp?: string;
    cvv?: string;
  }): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(
      SubscriptionGraphQL.mutations.reactivateOrganizationSubscription,
      { input },
    );
    return data.reactivateOrganizationSubscription;
  },

  /**
   * Convert a lapsed card-free trial into a paying subscription.
   * Shares ReactivateSubscriptionInput with reactivation — the payload is identical, only
   * the precondition differs (expired trial vs suspended organization).
   */
  async activateSubscriptionAfterTrial(input: {
    organizationId: string;
    billingCycle: string;
    planId?: string;
    useExistingPaymentMethod?: boolean;
    existingPaymentMethodId?: string;
    paymentMethodType?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phone?: string;
    ccNumber?: string;
    ccExp?: string;
    cvv?: string;
  }): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(
      SubscriptionGraphQL.mutations.activateSubscriptionAfterTrial,
      { input },
    );
    return data.activateSubscriptionAfterTrial;
  },

  /** How many free-trial organizations the current user owns, against the cap. */
  async getFreeTrialOrganizationUsage(): Promise<{
    used: number;
    limit: number;
    canCreateMore: boolean;
  }> {
    const data: any = await graphQlClient.request(
      SubscriptionGraphQL.queries.getFreeTrialOrganizationUsage,
    );
    return data.getFreeTrialOrganizationUsage;
  },

  /**
   * Get addon subscriptions for current organization (NEW SYSTEM)
   */
  async getAddonSubscriptions(): Promise<OrganizationSubscription[]> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getAddonSubscriptions);
    return data.getAddonSubscriptions;
  },

  /**
   * Get total included seats for current organization (NEW SYSTEM)
   */
  async getTotalIncludedSeats(): Promise<number> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getTotalIncludedSeats);
    return data.getTotalIncludedSeats;
  },

  /**
   * Add a new payment method
   */
  async addPaymentMethod(input: any): Promise<PaymentMethod> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.addPaymentMethod, {
      input,
    });
    return data.addPaymentMethod;
  },

  /**
   * Update payment method
   */
  async updatePaymentMethod(paymentMethodId: string, input: any): Promise<PaymentMethod> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.updatePaymentMethod, {
      paymentMethodId,
      input,
    });
    return data.updatePaymentMethod;
  },

  /**
   * Delete payment method
   */
  async deletePaymentMethod(paymentMethodId: string): Promise<{ deleted: boolean; warning?: string }> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.deletePaymentMethod, {
      paymentMethodId,
    });
    return data.deletePaymentMethod;
  },

  /**
   * Set payment method as default
   */
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<PaymentMethod> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.setDefaultPaymentMethod, {
      paymentMethodId,
    });
    return data.setDefaultPaymentMethod;
  },

  /**
   * Get subscription plan templates
   */
  async getSubscriptionPlans(): Promise<SubscriptionPlanTemplate[]> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getSubscriptionPlans);
    return data.getSubscriptionPlans;
  },

  /**
   * Get all subscription plan templates including add-ons (Admin)
   */
  async getAllSubscriptionPlans(): Promise<SubscriptionPlanTemplate[]> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getAllSubscriptionPlans);
    return data.getAllSubscriptionPlans;
  },

  /**
   * Get subscription plan by ID
   */
  async getSubscriptionPlanById(planId: string): Promise<SubscriptionPlanTemplate> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getSubscriptionPlanById, {
      planId,
    });
    return data.getSubscriptionPlanById;
  },

  /**
   * LEGACY: Get pricing configuration (DEPRECATED)
   */
  async getPricingConfig(): Promise<PricingConfig[]> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getPricingConfig);
    return data.getPricingConfig;
  },

  /**
   * LEGACY: Get pricing configuration by key (DEPRECATED)
   */
  async getPricingConfigByKey(key: string): Promise<PricingConfig> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getPricingConfigByKey, {
      key,
    });
    return data.getPricingConfigByKey;
  },

  /**
   * Assign plan to an organization (SUPER_ADMIN only)
   * This is an administrative operation that does NOT affect NMI billing
   */
  async assignPlanToOrganization(
    organizationId: string,
    planId: string,
    billingPeriod?: string,
  ): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(
      SubscriptionGraphQL.mutations.assignPlanToOrganization,
      {
        organizationId,
        planId,
        billingPeriod,
      },
    );
    return data.assignPlanToOrganization;
  },

  /**
   * Grant or extend a bounded free period (SUPER_ADMIN only). Never contacts NMI.
   * `trialEndsAt` is an ISO date-time (end of the chosen local day).
   */
  async extendOrganizationTrial(
    organizationId: string,
    trialEndsAt: string,
  ): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(
      SubscriptionGraphQL.mutations.extendOrganizationTrial,
      {
        organizationId,
        trialEndsAt,
      },
    );
    return data.extendOrganizationTrial;
  },

  /**
   * Start subscription billing (create NMI subscription)
   * Called by organization owners when they have an admin-assigned plan and want to start paying.
   * Requires a payment method to be set up first.
   */
  async startSubscription(): Promise<OrganizationSubscription> {
    const data: any = await graphQlClient.request(
      SubscriptionGraphQL.mutations.startSubscription,
    );
    return data.startSubscription;
  },

  /**
   * Sprint 17, story-082. The org's own current base plan's linked add-ons — used by the Add
   * Seats flow (does the org's plan have any linked add-ons, and at what price) instead of the
   * public getSubscriptionPlans, since an org on a private/since-unpublished plan still has
   * real, purchasable links here.
   */
  async getLinkedAddOnsForOrganization(): Promise<PlanAddonSummary[]> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.queries.getLinkedAddOnsForOrganization);
    return data.getLinkedAddOnsForOrganization;
  },

  /**
   * SUPER_ADMIN only: link an add-on to a plan at a given price.
   */
  async linkAddonToPlan(planId: string, addonId: string, priceCents: number): Promise<PlanAddonSummary> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.linkAddonToPlan, {
      planId,
      addonId,
      priceCents,
    });
    return data.linkAddonToPlan;
  },

  /**
   * SUPER_ADMIN only: unlink an add-on from a plan.
   */
  async unlinkAddonFromPlan(planId: string, addonId: string): Promise<boolean> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.unlinkAddonFromPlan, {
      planId,
      addonId,
    });
    return data.unlinkAddonFromPlan;
  },

  /**
   * SUPER_ADMIN only: change the price of an existing plan/add-on link.
   */
  async repriceAddonLink(planId: string, addonId: string, priceCents: number): Promise<PlanAddonSummary> {
    const data: any = await graphQlClient.request(SubscriptionGraphQL.mutations.repriceAddonLink, {
      planId,
      addonId,
      priceCents,
    });
    return data.repriceAddonLink;
  },
};

export default subscriptionService;
